"use client";

import {
  Checkbox,
  Panel,
  PillGroup,
  SelectField,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import type { DATA_AVAILABILITY_STATEMENT_GENERATOR_STRINGS } from "@/components/tools/data-availability-statement-generator";
import { SmallButton } from "@/components/tools/publishing-statements/kit";
import { t } from "@/lib/i18n/interpolate";
import {
  activeScenarios,
  type CodeInputs,
  type DataInputs,
  isCodeHostLink,
  isIncompleteDoi,
  type NoDataReason,
  type RepositoryInput,
  type RestrictionReason,
} from "@/lib/tool-kit/publishing/data-availability";

/*
 * The scenario panels of the data availability generator. Layout only: every word comes
 * from the widget's strings, passed down.
 */

type Strings = typeof DATA_AVAILABILITY_STATEMENT_GENERATOR_STRINGS;

export interface RepositoryRow extends RepositoryInput {
  id: number;
}

type Patch = (patch: Partial<DataInputs>) => void;

export function ArticlePanel({
  inputs,
  onChange,
  strings,
}: {
  inputs: DataInputs;
  onChange: Patch;
  strings: Strings;
}) {
  return (
    <Panel title={strings.articlePanel}>
      <Checkbox
        checked={inputs.supplementary}
        label={strings.supplementaryLabel}
        onChange={(supplementary) => onChange({ supplementary })}
      />
      {inputs.supplementary ? (
        <TextField
          id="das-files"
          label={strings.filesLabel}
          onChange={(files) => onChange({ files })}
          placeholder={strings.filesPlaceholder}
          value={inputs.files}
        />
      ) : null}
    </Panel>
  );
}

export function RepositoryPanel({
  rows,
  onRowChange,
  onAdd,
  onRemove,
  strings,
}: {
  rows: readonly RepositoryRow[];
  onRowChange: (id: number, patch: Partial<RepositoryInput>) => void;
  onAdd: () => void;
  onRemove: (id: number) => void;
  strings: Strings;
}) {
  return (
    <div className="space-y-4">
      {rows.map((row, index) => (
        <Panel
          key={row.id}
          title={t(strings.repositoryPanel, { number: index + 1 })}
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              id={`das-repo-${row.id}`}
              label={strings.repositoryLabel}
              onChange={(repository) => onRowChange(row.id, { repository })}
              placeholder={strings.repositoryPlaceholder}
              value={row.repository}
            />
            <TextField
              hint={strings.identifierHint}
              id={`das-id-${row.id}`}
              label={strings.identifierLabel}
              mono
              onChange={(identifier) => onRowChange(row.id, { identifier })}
              placeholder={strings.identifierPlaceholder}
              value={row.identifier}
            />
            <TextField
              id={`das-desc-${row.id}`}
              label={strings.descriptionLabel}
              onChange={(description) => onRowChange(row.id, { description })}
              placeholder={strings.descriptionPlaceholder}
              value={row.description}
            />
            <TextField
              id={`das-license-${row.id}`}
              label={strings.licenseLabel}
              onChange={(license) => onRowChange(row.id, { license })}
              placeholder={strings.licensePlaceholder}
              value={row.license}
            />
            <TextField
              id={`das-embargo-${row.id}`}
              label={strings.embargoLabel}
              onChange={(embargo) => onRowChange(row.id, { embargo })}
              type="date"
              value={row.embargo}
            />
          </div>
          {rows.length > 1 ? (
            <SmallButton
              ariaLabel={t(strings.removeRepositoryAria, { number: index + 1 })}
              label={strings.removeRepository}
              onClick={() => onRemove(row.id)}
            />
          ) : null}
        </Panel>
      ))}
      <SmallButton label={strings.addRepository} onClick={onAdd} />
    </div>
  );
}

export function PublicPanel({
  inputs,
  onChange,
  strings,
}: {
  inputs: DataInputs;
  onChange: Patch;
  strings: Strings;
}) {
  return (
    <Panel title={strings.publicPanel}>
      <TextAreaField
        id="das-sources"
        label={strings.sourcesLabel}
        onChange={(publicSources) => onChange({ publicSources })}
        placeholder={strings.sourcesPlaceholder}
        prose
        rows={3}
        value={inputs.publicSources}
      />
    </Panel>
  );
}

export function RestrictedPanel({
  inputs,
  onChange,
  strings,
}: {
  inputs: DataInputs;
  onChange: Patch;
  strings: Strings;
}) {
  return (
    <Panel title={strings.restrictedPanel}>
      <div className="grid gap-4 sm:grid-cols-2">
        <SelectField<RestrictionReason>
          id="das-restriction"
          label={strings.restrictionLabel}
          onChange={(restriction) => onChange({ restriction })}
          options={[
            { value: "privacy", label: strings.restrictionPrivacy },
            { value: "ethics", label: strings.restrictionEthics },
            { value: "thirdParty", label: strings.restrictionThirdParty },
            { value: "legal", label: strings.restrictionLegal },
          ]}
          value={inputs.restriction}
        />
        <TextField
          id="das-restricted-what"
          label={strings.restrictedDescriptionLabel}
          onChange={(restrictedDescription) =>
            onChange({ restrictedDescription })
          }
          placeholder={strings.restrictedDescriptionPlaceholder}
          value={inputs.restrictedDescription}
        />
        {inputs.restriction === "thirdParty" ? (
          <TextField
            id="das-party"
            label={strings.partyLabel}
            onChange={(party) => onChange({ party })}
            placeholder={strings.partyPlaceholder}
            value={inputs.party}
          />
        ) : null}
        <TextField
          id="das-access"
          label={strings.accessContactLabel}
          onChange={(accessContact) => onChange({ accessContact })}
          placeholder={strings.accessContactPlaceholder}
          value={inputs.accessContact}
        />
        <TextField
          id="das-access-details"
          label={strings.accessDetailsLabel}
          onChange={(accessDetails) => onChange({ accessDetails })}
          placeholder={strings.accessDetailsPlaceholder}
          value={inputs.accessDetails}
        />
        <TextField
          id="das-conditions"
          label={strings.conditionsLabel}
          onChange={(conditions) => onChange({ conditions })}
          placeholder={strings.conditionsPlaceholder}
          value={inputs.conditions}
        />
      </div>
    </Panel>
  );
}

export function RequestPanel({
  inputs,
  onChange,
  strings,
}: {
  inputs: DataInputs;
  onChange: Patch;
  strings: Strings;
}) {
  return (
    <Panel title={strings.requestPanel}>
      <div className="grid gap-4 sm:grid-cols-2">
        <TextField
          id="das-request-contact"
          label={strings.requestContactLabel}
          onChange={(requestContact) => onChange({ requestContact })}
          placeholder={strings.correspondingAuthor}
          value={inputs.requestContact}
        />
        <TextField
          id="das-request-reason"
          label={strings.requestReasonLabel}
          onChange={(requestReason) => onChange({ requestReason })}
          placeholder={strings.requestReasonPlaceholder}
          value={inputs.requestReason}
        />
      </div>
    </Panel>
  );
}

export function NonePanel({
  inputs,
  onChange,
  strings,
}: {
  inputs: DataInputs;
  onChange: Patch;
  strings: Strings;
}) {
  return (
    <Panel title={strings.nonePanel}>
      <PillGroup<NoDataReason>
        label={strings.noneReasonLabel}
        name="das-none-reason"
        onChange={(noDataReason) => onChange({ noDataReason })}
        options={[
          { value: "noData", label: strings.noneNoDataOption },
          { value: "theoretical", label: strings.noneTheoreticalOption },
        ]}
        value={inputs.noDataReason}
      />
    </Panel>
  );
}

export function CodeFields({
  code,
  onChange,
  strings,
}: {
  code: CodeInputs;
  onChange: (patch: Partial<CodeInputs>) => void;
  strings: Strings;
}) {
  if (code.mode === "repository") {
    return (
      <div className="grid gap-4 sm:grid-cols-2">
        <TextField
          id="das-code-repo"
          label={strings.repositoryLabel}
          onChange={(repository) => onChange({ repository })}
          placeholder={strings.codeRepositoryPlaceholder}
          value={code.repository}
        />
        <TextField
          id="das-code-id"
          label={strings.identifierLabel}
          mono
          onChange={(identifier) => onChange({ identifier })}
          placeholder={strings.codeIdentifierPlaceholder}
          value={code.identifier}
        />
        <TextField
          id="das-code-desc"
          label={strings.codeDescriptionLabel}
          onChange={(description) => onChange({ description })}
          placeholder={strings.codeDescriptionPlaceholder}
          value={code.description}
        />
        <TextField
          id="das-code-license"
          label={strings.licenseLabel}
          onChange={(license) => onChange({ license })}
          placeholder={strings.codeLicensePlaceholder}
          value={code.license}
        />
      </div>
    );
  }
  if (code.mode === "request") {
    return (
      <div className="grid gap-4 sm:grid-cols-2">
        <TextField
          id="das-code-desc"
          label={strings.codeDescriptionLabel}
          onChange={(description) => onChange({ description })}
          placeholder={strings.codeDescriptionPlaceholder}
          value={code.description}
        />
        <TextField
          id="das-code-contact"
          label={strings.requestContactLabel}
          onChange={(contact) => onChange({ contact })}
          placeholder={strings.correspondingAuthor}
          value={code.contact}
        />
      </div>
    );
  }
  if (code.mode === "none") {
    return (
      <TextField
        id="das-code-software"
        label={strings.softwareLabel}
        onChange={(software) => onChange({ software })}
        placeholder={strings.softwarePlaceholder}
        value={code.software}
      />
    );
  }
  return null;
}

/** Advice under the statements: placeholders left, policy problems, fragile links. */
export function dataNotices(
  inputs: DataInputs,
  repositories: readonly RepositoryRow[],
  code: CodeInputs,
  output: string,
  strings: Strings
): string[] {
  const active = activeScenarios(inputs.scenarios);
  const notes: string[] = [];
  const placeholders = [
    strings.placeholderRepository,
    strings.placeholderIdentifier,
    strings.placeholderSources,
    strings.placeholderParty,
    strings.placeholderContact,
    strings.placeholderDate,
  ];
  if (placeholders.some((placeholder) => output.includes(placeholder))) {
    notes.push(strings.noticePlaceholders);
  }
  if (active.includes("request")) {
    notes.push(strings.noticeRequestPlos);
  }
  if (
    active.includes("repository") &&
    repositories.some((row) => row.identifier.trim() === "")
  ) {
    notes.push(strings.noticeIdentifier);
  }
  const identifiers = [
    ...(active.includes("repository")
      ? repositories.map((row) => row.identifier)
      : []),
    ...(code.mode === "repository" ? [code.identifier] : []),
  ];
  for (const value of identifiers.filter(isIncompleteDoi)) {
    notes.push(t(strings.noticeIncompleteDoi, { value: value.trim() }));
  }
  if (identifiers.some(isCodeHostLink)) {
    notes.push(strings.noticeCodeHost);
  }
  if (code.mode === "repository" || code.mode === "request") {
    notes.push(strings.noticeNature);
  }
  return notes;
}
